import React from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from "react-router-dom";
import { Divider, Popover, PopoverContent, PopoverTrigger, Modal, ModalContent, ModalBody } from "@heroui/react";

import { store, useAppSelector } from '@/app/store';
import { TagInfo, copyTagDescriptor, copyTagsHolder, getNewTagDescriptor } from '@/features/tags/tagsSlice';

import Button from '@/components/Common/Button';
import FlexRow from '@/components/Common/FlexRow';
import Grid from '@/components/Common/Grid';
import Input from '@/components/Common/Input';
import MenuItem from '@/components/Common/MenuItem';
import { NumberText } from '@/components/Common/Field';
import { DeleteModal, RightBar } from '@/components/Common/Modals';
import { TextArea } from '@/components/Common/Input';

import { CatHeader } from '@/pages/Projects/Project';
import Tag from './Tag';
import { RoleContext } from '@/app/api/common';
import { usePageSubscription } from '@/app/websocket/hooks';

const Tags = () => {
  const { t } = useTranslation();

  usePageSubscription([{ type: 'tag' }]);

  const tags = useAppSelector((state) => state.tags);
  const navigate = useNavigate();
  const { tag_id } = useParams();
  const [cols, setCols] = React.useState([] as any[]);
  const [rows, setRows] = React.useState([] as TagInfo[]);
  const [deleteNum, setDeleteNum] = React.useState(-1);
  const role = React.useContext(RoleContext);

  const tagNum = React.useMemo(() => {
    if (!tag_id) return -1;
    if (tag_id === '__new__') return -2;
    const idx = tags?.tags?.findIndex((t: any) => t.tag === tag_id);
    return idx !== undefined && idx >= 0 ? idx : -1;
  }, [tag_id, tags.tags]);

  React.useEffect(() => {
    store.getTags();
  }, []);

  React.useEffect(() => {
    setCols([
    {
        field:			"tag",
        headerName:		t("tags:tag_name"),
        flex:			1,
        minWidth:		200,
    },
    {
        field:			"desc",
        headerName:		t("tags:description"),
        flex:			1,
        minWidth:		300,
    },
    ...(role?.admin || role?.privilages?.includes("ChangeTags") || role?.privilages?.includes("DeleteTags") ? [{
        field:			"options",
        headerName:		t("tags:options"),
        flex:			0,
        filter:			false,
        minWidth:		90,
        maxWidth:		90,
        cellRenderer:	OptionsCell,
    }] : []),
    ]);

    const rws: TagInfo[] = [];
    for (let i = 0; i < tags?.tags?.length; i++) {
      let tag = copyTagDescriptor(tags?.tags[i]);
      let tagAny = tag as any;

      tagAny.requestDelete = () => setDeleteNum(i);
      tagAny.requestEdit = () => navigate('/settings/tags/' + encodeURIComponent(tag.tag));
      tagAny.__index__ = i;

      rws.push(tag);
    }

    setRows(rws);
  }, [tags.tags, t]);

  const updateIsOpen = (val: boolean) => {
    if (!val)
      navigate('/settings/tags');
  };

  const processDelete = React.useCallback(async (_obj: Object, answer: string) => {
    setDeleteNum(-1);

    if (answer !== "yes") {
      return;
    }

    if (deleteNum < -1)
      return;

    const ntags = copyTagsHolder(tags);
    if (	!ntags ||
        	ntags?.tags?.length <= deleteNum
       )
      return;

    ntags.tags.splice(deleteNum, 1);

    const mess = await store.setTags(ntags);
    console.log("Tags/processDelete/deleteNum=" + deleteNum + "/mess=", mess);
  }, [tags.tags, deleteNum]);

  const updateTag = React.useCallback(async (n: number, tag: TagInfo) => {
    const ntags = copyTagsHolder(tags);
    if (	!tag ||
    		n == -1 ||
    		!ntags ||
        	ntags?.tags?.length <= n
       )
      return;

    const ntag = copyTagDescriptor(tag);
    if (n == -2) {
      ntags?.tags.push(ntag);
    } else {
      ntags.tags[n] = ntag;
    }

    const mess = await store.setTags(ntags);
    console.log("Tags/updateTag/n=" + n + "/mess=", mess);
  }, [tags.tags]);

  const handleCellClick = React.useCallback((evt: any) => {
    if (evt.colDef.field == "options")
      return;
    if(!role?.admin && !role?.privilages?.includes("ChangeTags"))
      return
    navigate('/settings/tags/' + encodeURIComponent(evt.data.tag));
  }, [navigate]);

  const getTagDesc = React.useCallback((n: number): TagInfo | null => {
    if (n == -2)
      return getNewTagDescriptor();

    if (	n < 0 ||
    		n >= rows?.length
       )
      return null;

    return rows[n];
  }, [rows]);

  return (
  <React.Fragment>
    <FlexRow direction="column"
	         align="start"
	         className={[
               'bg-white',
               'px-[32px] pt-[32px] sticky top-[64px] z-10',
             ]}>
      <div className="text-[32px]/[48px]">
        { t('tags:tags') }
      </div>
      <div>
        <NumberText>
          { rows?.length }
        </NumberText>
        { ' ' +
          t('tags:new_tags')
        }
      </div>
      <Divider className="my-[8px]" />
      {(role?.admin || role?.privilages?.includes("ChangeTags")) &&
        <Button caption={t('tags:new_tags')}
                icon="add"
                className="uppercase mt-[20px]"
                onClick={() => navigate('/settings/tags/__new__')} />
      }
    </FlexRow>
    <FlexRow direction="column"
             align="start"
             className={[
               "py-[16px]",
               "px-[32px]"
             ]}>

      <Grid rowData={rows}
            columnDefs={cols}
            filter={false}
            globalFilter={true}
            showPages={true}
            maxHeight="calc(100vh - 380px)"
            onCellClicked={handleCellClick}
            pageSize={10} />
    </FlexRow>
          {tagNum != -1 &&
              <RightBar onOpenChange={updateIsOpen}>
                  <Tag tag={getTagDesc(tagNum)}
                      index={tagNum}
                      setTag={(tag: TagInfo) => updateTag(tagNum, tag)}
                      onClose={() => navigate('/settings/tags')} />
              </RightBar>
          }
  {deleteNum > -1 && deleteNum < rows.length &&
	<DeleteModal title={t('tags:delete_tag_:caption')}
	             message={t('tags:delete_tag_:message_n', {n: rows[deleteNum]?.tag})}
	             onResult={processDelete}>
      <>
      </>
    </DeleteModal>
  }
  </React.Fragment>
  );
};

const OptionsCell = (props: any) => {
  const { t } = useTranslation();

  const [open, setOpen] = React.useState(false);
  const role = React.useContext(RoleContext);
  const handleEdit = React.useCallback(() => {
    setOpen(false);
    props.data.requestEdit();
  }, [setOpen, props.data]);

  const handleDelete = React.useCallback(async () => {
    setOpen(false);
    props.data.requestDelete();
  }, [setOpen, props.data]);
  return (
  <Popover key={1}
           isOpen={open}
           placement="bottom-end"
           classNames={{
             content: "rounded-none",
           }}
           onOpenChange={setOpen}>
    <PopoverTrigger>
      <div className="h-full w-full text-right">
        <span className="material-icons cursor-pointer !text-[16px] !text-black align-middle">
          more_vert
        </span>
      </div>
    </PopoverTrigger>
    <PopoverContent className="p-0">
      <FlexRow flex={0}
               direction="column"
               align="start"
               className="bg-white text-black min-w-[205px] py-[8px] gap-[8px]">
        {( role?.admin || role?.privilages?.includes("ChangeTags"))&&
          <MenuItem icon="edit_note"
                    label={t('tags:edit_tag_information:caption')}
                    onClick={handleEdit} />
        }
        {(role?.admin || role?.privilages?.includes("DeleteTags")) &&
          <MenuItem icon="delete"
                    color="!text-[--Warning]"
                    label={t('tags:delete_tag')}
                    onClick={handleDelete} />
        }
      </FlexRow>
    </PopoverContent>
  </Popover>
  );
};



export default Tags;
